<template>
  <div class="app-container">
    <div class="card card-custom mb-5">
      <div class="card-body">
        <!-- Calendar table -->
        <div class="calendar-container">
          <div class="calendar-wrapper">
            <table class="calendar-table">
              <colgroup>
                <col class="week-column" />
                <col
                  v-for="index in 7"
                  :key="`col-${index}`"
                  class="day-column"
                />
              </colgroup>
              <thead>
                <tr class="header-row">
                  <th class="week-cell">Tuần</th>
                  <th
                    v-for="day in days"
                    :key="`day-${day}`"
                    class="day-header"
                  >
                    {{ day }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="week in numberOfWeeks"
                  :key="week"
                  class="calendar-row"
                >
                  <td class="week-cell">Tuần {{ week }}</td>
                  <td
                    v-for="(day, index) in getWeekDays(week)"
                    :key="`${week}-${index}`"
                    class="day-cell"
                  >
                    <p class="day-date">{{ day.date }}</p>
                    <div class="event-container" ref="eventContainer">
                      <div
                        v-for="event in getEventsStartingOnDay(week, day.date)"
                        :key="event.id"
                        class="event-card"
                        :style="{
                          backgroundColor: event.color || '#F4B183',
                          color: getContrastColor(event.color || '#F4B183'),
                        }"
                        @click.stop="openContextMenu($event, event)"
                      >
                        <div class="event-content">
                          <span class="event-title">{{ event.title }}</span>
                          <span class="svg-icon">
                            <inline-svg
                              v-if="
                                event.status === 1 &&
                                event.questionSurveys == null
                              "
                              :src="'/media/svg/icons/Neolex/Basic/check.svg'"
                            />
                            <span v-if="event.questionSurveys != null">
                              {{ event.questionSurveys }}
                            </span>
                            <span v-if="event.bloodSugarCount != null">
                              {{ event.bloodSugarCount }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Context Menu -->
    <div
      v-if="showContextMenu"
      class="context-menu"
      :style="{ top: menuPosition.top + 'px', left: menuPosition.left + 'px' }"
      @click.stop
    >
      <div
        v-if="selectedEvent.questionSurveys != null"
        class="context-menu-item navi-link text-primary my-1"
        @click="viewSurveyResult(selectedEvent)"
      >
        <span class="menu-icon svg-icon svg-icon-sm">
          <inline-svg
            class="svg-icon"
            src="/media/svg/icons/Neolex/Basic/eye.svg"
          />
        </span>
        <span class="navi-text text-primary">Xem kết quả</span>
      </div>
      <hr class="m-1" />
      <div
        class="context-menu-item navi-link text-primary my-1"
        @click="showChangeDateModal(selectedEvent)"
      >
        <span class="menu-icon svg-icon svg-icon-sm">
          <inline-svg
            class="svg-icon"
            src="/media/svg/icons/Neolex/Basic/calendar.svg"
          />
        </span>
        <span class="navi-text text-primary">Thay đổi ngày</span>
      </div>
      <hr class="m-1" />
      <div
        class="context-menu-item navi-link text-primary my-1"
        @click="editActivity(selectedEvent)"
      >
        <span class="menu-icon svg-icon svg-icon-sm">
          <inline-svg
            class="svg-icon"
            src="/media/svg/icons/Neolex/Basic/edit-2.svg"
          />
        </span>
        <span class="navi-text text-primary">Chỉnh sửa</span>
      </div>
      <hr class="m-1" />
      <div
        class="context-menu-item navi-link text-danger my-1"
        @click="deleteActivity(selectedEvent.activityModuleMappingId)"
      >
        <span class="menu-icon svg-icon svg-icon-sm">
          <inline-svg
            class="svg-icon"
            src="/media/svg/icons/Neolex/Basic/circle-x.svg"
          />
        </span>
        <span class="navi-text text-danger">Xóa</span>
      </div>
    </div>

    <!-- Change Date Modal -->
    <b-modal
      id="change-date-modal"
      ref="changeDateModal"
      title="Chọn ngày mới"
      size="sm"
      hide-footer
      @hidden="resetChangeDateForm"
    >
      <div class="form-container">
        <basic-date-picker
          v-model="changeDateForm.date"
          label="Ngày mới"
          placeholder="Chọn ngày"
          name="newDate"
          format="YYYY-MM-DD"
        />
        <div class="modal-actions mt-4 mr-3">
          <b-button variant="secondary" @click="hideChangeDateModal">
            Hủy
          </b-button>
          <b-button
            variant="success"
            @click="handleChangeDate"
            class="mr-2"
            :disabled="!changeDateForm.date"
          >
            Xác nhận
          </b-button>
        </div>
      </div>
    </b-modal>

    <ModalSurveyResult
      :id="selectedSurvey"
      :patientId="patientId"
      :accountId="accountId"
      :ownPackage="this.ownPackage"
      @close="selectedSurvey = null"
    />
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ScheduleTranUserPackageDay',
  components: {
    ModalSurveyResult: () => import('./modal/ModalSurveyResult.vue'),
  },

  props: {
    moduleId: {
      type: [String, Number],
      default: null,
    },
    moduleData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      days: [
        'Thứ hai',
        'Thứ ba',
        'Thứ tư',
        'Thứ năm',
        'Thứ sáu',
        'Thứ bảy',
        'Chủ nhật',
      ],
      events: [],
      loading: false,
      defaultColor: '#16A45A',
      numberOfWeeks: 0,
      dateWeek: [],
      actualDays: [],
      showContextMenu: false,
      selectedEvent: null,
      menuPosition: { top: 0, left: 0 },
      selectedSurvey: null,
      patientId: null,
      accountId: null,
      ownPackage: null,
      changeDateForm: {
        date: null,
        event: null,
      },
    };
  },
  watch: {
    moduleData: {
      handler(newData) {
        if (
          newData &&
          newData.activities &&
          Array.isArray(newData.activities) &&
          newData.actualDays
        ) {
          this.actualDays = newData.actualDays;
          this.numberOfWeeks = newData.totalWeek || Math.ceil(newData.actualDays.length / 7) || 1;
          this.groupDatesByWeek(newData.actualDays);
          this.processActivities(newData.activities);
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getContrastColor(hexColor) {
      hexColor = hexColor.replace('#', '');
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance < 0.5 ? '#FFFFFF' : '#000000';
    },

    getWeekDays(week) {
      const startIndex = (week - 1) * 7;
      return this.actualDays.slice(startIndex, startIndex + 7).map((date) => ({
        date,
        weekday: moment(date, 'DD/MM/YYYY').format('dddd'),
      }));
    },

    getEventsStartingOnDay(week, date) {
      return this.events
        .filter((e) => {
          if (!e.actualDate) {
            console.warn(`Event with id ${e.id} has undefined actualDate`);
            return false;
          }
          return e.week === week && e.actualDate === date;
        })
        .sort((a, b) => a.id.localeCompare(b.id));
    },

    groupDatesByWeek(dates) {
      this.dateWeek = [];
      for (let i = 0; i < dates.length; i += 7) {
        const week = dates.slice(i, i + 7);
        if (week.length > 0) {
          this.dateWeek.push(`${week[0]} - ${week[week.length - 1]}`);
        }
      }
    },

    processActivities(activities) {
      if (!Array.isArray(activities)) {
        console.error('Activities is not an array:', activities);
        this.events = [];
        return;
      }
      this.events = activities.map((item) => {
        const startAtDay = Number(item.startAtDay);
        if (!startAtDay || startAtDay < 1) {
          console.warn(
            `Invalid startAtDay for item ${item.id}: ${item.startAtDay}`,
          );
          return {
            id: item.id,
            accountId: item.accountId,
            activityId: item.activityId,
            week: 1,
            actualDate: this.actualDays[0] || item.actualDate || '',
            title: item.name || 'Không lấy được',
            color: item.color || this.defaultColor,
            activityModuleMappingId: item.activityModuleMappingId || null,
            surveyId: item.surveyId || null,
            status: item.status || null,
            questionSurveys: item.questionSurveys || null,
            bloodSugarCount: item.bloodSugarCount || null,
          };
        }

        const week = Math.floor((startAtDay - 1) / 7) + 1;
        const dayOfWeekIndex = (startAtDay - 1) % 7;
        const actualDate =
          this.actualDays[(week - 1) * 7 + dayOfWeekIndex] || item.actualDate;

        return {
          id: item.id,
          accountId: item.accountId,
          activityId: item.activityId,
          week: week,
          actualDate: actualDate,
          title: item.name || 'Không lấy được',
          color: item.color || this.defaultColor,
          activityModuleMappingId: item.activityModuleMappingId || null,
          status: item.status || null,
          surveyId: item.surveyId || null,
          questionSurveys: item.questionSurveys || null,
          bloodSugarCount: item.bloodSugarCount || null,
        };
      });
    },

    addActivity(activityData) {
      const week = Math.floor((activityData.startAtDay - 1) / 7) + 1;
      const dayOfWeekIndex = (activityData.startAtDay - 1) % 7;
      const actualDate =
        this.actualDays[(week - 1) * 7 + dayOfWeekIndex] ||
        activityData.actualDate;
      const newEvent = {
        id: activityData.id || `temp-${Date.now()}`,
        activityId: activityData.activityId,
        week: week,
        actualDate: actualDate,
        title: activityData.name,
        color: activityData.color || this.defaultColor,
        activityModuleMappingId: activityData.activityModuleMappingId,
      };

      this.events.push(newEvent);
    },

    updateActivity(activityData) {
      const index = this.events.findIndex(
        (e) =>
          e.activityModuleMappingId === activityData.activityModuleMappingId,
      );

      if (index !== -1) {
        const week = Math.floor((activityData.startAtDay - 1) / 7) + 1;
        const dayOfWeekIndex = (activityData.startAtDay - 1) % 7;
        const actualDate =
          this.actualDays[(week - 1) * 7 + dayOfWeekIndex] ||
          activityData.actualDate;

        this.events[index] = {
          ...this.events[index],
          week: week,
          actualDate: actualDate,
          title: activityData.name,
          color: activityData.color || this.defaultColor,
        };

        this.events = [...this.events];
      }
    },

    deleteActivity(activityModuleMappingId) {
      this.$swal({
        title: 'Xác nhận',
        text: 'Bạn có chắc chắn muốn xóa hoạt động này?',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Hủy',
            value: false,
            visible: true,
            className: 'btn btn-secondary',
            closeModal: true,
          },
          confirm: {
            text: 'Xóa',
            value: true,
            visible: true,
            className: 'btn btn-danger',
            closeModal: true,
          },
        },
      }).then((result) => {
        if (result) {
          this.events = this.events.filter(
            (e) => e.activityModuleMappingId !== activityModuleMappingId,
          );
          this.$emit('delete-activity', activityModuleMappingId);
          this.closeContextMenu();
        }
      });
    },

    editActivity(event) {
      this.$emit('edit-activity', event);
      this.closeContextMenu();
    },

    showChangeDateModal(event) {
      this.changeDateForm.event = { ...event };
      this.changeDateForm.date = moment(event.actualDate, 'DD/MM/YYYY').format(
        'YYYY-MM-DD',
      );
      this.$refs.changeDateModal.show();
      this.closeContextMenu();
    },

    hideChangeDateModal() {
      this.$refs.changeDateModal.hide();
    },

    resetChangeDateForm() {
      this.changeDateForm.date = null;
      this.changeDateForm.event = null;
    },

    async handleChangeDate() {
      if (!this.changeDateForm.date) {
        this.$swal('Lỗi!', 'Vui lòng chọn ngày', 'error');
        return;
      }

      const selectedDate = moment(this.changeDateForm.date);
      const today = moment().startOf('day');
      if (selectedDate.isBefore(today)) {
        this.$swal(
          'Lỗi!',
          'Ngày chọn phải lớn hơn hoặc bằng ngày hiện tại',
          'error',
        );
        return;
      }

      this.$swal({
        title: 'Xác nhận thay đổi ngày',
        text: `Bạn có chắc chắn muốn thay đổi ngày thành ${moment(
          this.changeDateForm.date,
        ).format('DD/MM/YYYY')}?`,
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Hủy',
            value: false,
            visible: true,
            className: 'btn btn-secondary',
            closeModal: true,
          },
          confirm: {
            text: 'Xác nhận',
            value: true,
            visible: true,
            className: 'btn btn-success',
            closeModal: true,
          },
        },
      }).then(async (result) => {
        if (result) {
          try {
            this.loading = true;
            const date = new Date(`${this.changeDateForm.date}T00:00:00Z`);
            const epochTimestampInSeconds = Math.floor(date.getTime() / 1000);
            await this.$api.put(
              `TranProgramActivity?id=${this.changeDateForm.event.activityId}&changeDate=${epochTimestampInSeconds}`,
            );
            this.resetChangeDateForm();
            this.$emit('reset-request');
            this.hideChangeDateModal();
          } catch (error) {
            console.error('Error changing date:', error);
            this.$swal('Lỗi!', 'Đã có lỗi xảy ra khi thay đổi ngày', 'error');
          } finally {
            this.loading = false;
          }
        }
      });
    },
    calculateWeekForDate(date) {
      const startOfWeek = moment(this.actualDays[0], 'DD/MM/YYYY').startOf(
        'isoWeek',
      );
      const diffDays = date.diff(startOfWeek, 'days') + 1;
      return Math.floor((diffDays - 1) / 7) + 1;
    },

    openContextMenu(event, selectedEvent) {
      this.selectedEvent = selectedEvent;
      const rect = event.currentTarget.getBoundingClientRect();
      this.menuPosition = {
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      };
      this.showContextMenu = true;
      setTimeout(() => {
        document.addEventListener('click', this.handleOutsideClick);
      }, 0);
    },

    closeContextMenu() {
      this.showContextMenu = false;
      this.selectedEvent = null;
      document.removeEventListener('click', this.handleOutsideClick);
    },

    handleOutsideClick(event) {
      if (
        !this.$el.querySelector('.context-menu')?.contains(event.target) &&
        !event.target.closest('.event-card')
      ) {
        this.closeContextMenu();
      }
    },

    async viewSurveyResult(selectedEvent) {
      this.selectedSurvey = selectedEvent.surveyId;
      const { data } = await this.$api.get(
        `CustomerReceives/by-account/${selectedEvent.accountId}`,
      );
      this.patientId = data.patientId;
      this.accountId = data.accountId;
      await this.loadPrepareData();
      this.closeContextMenu();
      await this.delay(50);
      this.$bvModal.show('modal-survey-result');
    },

    async loadPrepareData() {
      const { meta, data, error } = await this.$api.get(
        '/UserDashboard/GetAccountId',
        {
          params: {
            id: this.patientId,
          },
        },
      );
      if (!meta.success) {
        return this.showToastrMessage(error.message, 'error');
      }
      if (data) {
        this.ownPackage = data.ownPackage.packageAccountTransactionId;
      }
    },

    updateNumberOfWeeks(newNumberOfWeeks) {
      this.numberOfWeeks = newNumberOfWeeks;
    },

    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
  },
  mounted() {},
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
};
</script>

<style scoped>
.app-container {
  padding: 0;
}

.calendar-container {
  width: 100%;
  position: relative;
}

.calendar-wrapper {
  width: 100%;
  scrollbar-width: thin;
  scroll-behavior: smooth;
}

.calendar-table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}

.week-column {
  width: 80px;
}

.day-column {
  width: calc((100% - 80px) / 7);
}

.day-date {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  color: #333;
  line-height: 18px;
}

.event-container {
  height: calc(100% - 20px);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.header-row th {
  padding: 8px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  white-space: nowrap;
}

.day-header {
  width: calc((100% - 80px) / 7);
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-cell {
  width: 80px;
  text-align: center;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
}

.day-cell {
  border: 1px solid #ddd;
  vertical-align: top;
  height: 100px;
  padding: 4px;
  width: calc((100% - 80px) / 7);
}

.event-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.event-card {
  padding: 4px 8px;
  font-size: 12px;
  height: auto;
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-bottom: 5px;
  cursor: pointer;
}

.event-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-weight: 500;
}

.context-menu {
  position: absolute;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  width: 150px;
  min-height: 50px;
}

.context-menu-item {
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.context-menu-item:hover {
  background-color: #f5f5f5;
}

.context-menu-item.delete:hover {
  background-color: #ffebee;
}

.date-size {
  margin: auto;
  font-size: 10px;
  max-width: 60px;
}

.form-container {
  padding: 10px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
}

/* Responsive styling */
@media (max-width: 992px) {
  .week-column {
    width: 60px;
  }

  .week-cell {
    width: 60px;
  }

  .day-column {
    width: calc((100% - 60px) / 7);
  }

  .day-cell {
    width: calc((100% - 60px) / 7);
    height: 60px;
  }

  .event-card {
    padding: 2px 4px;
    font-size: 10px;
  }

  .context-menu {
    width: 120px;
  }

  .context-menu-item {
    font-size: 12px;
    padding: 6px 10px;
  }
}
</style>
